import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const HostelLaundry = ({ onNavigate, userInfo }) => {
  const [serviceFilter, setServiceFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [laundryOrders, setLaundryOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userPrivileges, setUserPrivileges] = useState([]);
  const [privilegesLoading, setPrivilegesLoading] = useState(true);

  useEffect(() => {
    if (userInfo?.userId) {
      fetchDashboardData();
      fetchUserPrivileges();
      fetchLaundryOrders();
      fetchLaundryStats();
    }
  }, [userInfo]);

  useEffect(() => {
    if (userInfo?.userId) {
      const timer = setTimeout(() => {
        fetchLaundryOrders();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [serviceFilter, paymentFilter, statusFilter, searchQuery]);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/dashboard/${userInfo.userId}`);
      const data = await response.json();
      if (data.success) {
        setDashboardData(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  };

  const fetchUserPrivileges = async () => {
    try {
      setPrivilegesLoading(true);
      const response = await fetch(`${API_URL}/admin/user-privileges/${userInfo.userId}`);
      const data = await response.json();
      
      if (data.success && data.privileges) {
        setUserPrivileges(data.privileges);
      } else {
        setUserPrivileges([]);
      }
    } catch (err) {
      console.error('Error fetching privileges:', err);
      setUserPrivileges([]);
    } finally {
      setPrivilegesLoading(false);
    }
  };

  const getPrivilegesByCategory = (category) => {
    return userPrivileges.filter(privilege => privilege.page_category === category);
  };

  // Check if user has access to this page
  useEffect(() => {
    if (!privilegesLoading && userPrivileges && userPrivileges.length > 0) {
      const hasAccess = userPrivileges.some(
        privilege => privilege.page_route === 'hostel-admin-laundry'
      );
      
      console.log('Access check for laundry page:', { 
        hasAccess, 
        privilegeCount: userPrivileges.length,
        routes: userPrivileges.map(p => p.page_route)
      });
      
      if (!hasAccess) {
        setTimeout(() => {
          alert('⚠️ Access Denied: You do not have permission to view this page.');
          if (onNavigate) {
            onNavigate('hostel-admin-dashboard');
          }
        }, 100);
      }
    }
  }, [privilegesLoading, userPrivileges]);

  const fetchLaundryOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      if (serviceFilter !== 'all') queryParams.append('serviceFilter', serviceFilter);
      if (paymentFilter !== 'all') queryParams.append('paymentFilter', paymentFilter);
      if (statusFilter !== 'all') queryParams.append('statusFilter', statusFilter);
      if (searchQuery) queryParams.append('search', searchQuery);

      const url = `${API_URL}/admin/laundry/orders/${userInfo.userId}?${queryParams}`;
      console.log('Fetching laundry orders from:', url);
      
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('Laundry Orders Response:', data);
      console.log('Total orders received:', data.orders?.length || 0);
      if (data.orders && data.orders.length > 0) {
        console.log('Sample order structure:', data.orders[0]);
        console.log('Order status field:', data.orders[0].order_status || data.orders[0].status);
        const uniqueStudents = [...new Set(data.orders.map(o => o.student_name))];
        console.log('Unique students:', uniqueStudents);
        console.log('Number of unique students:', uniqueStudents.length);
      }

      if (data.success) {
        setLaundryOrders(data.orders || []);
      } else {
        setError(data.message || 'Failed to load laundry orders');
      }
    } catch (err) {
      console.error('Error fetching laundry orders:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchLaundryStats = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/laundry/stats/${userInfo.userId}`);
      const data = await response.json();
      console.log('Laundry Stats Response:', data);
      if (data.success) {
        console.log('Stats data:', data.statistics);
        setStats(data.statistics);
      } else {
        console.error('Failed to fetch stats:', data.message);
      }
    } catch (err) {
      console.error('Error fetching laundry stats:', err);
    }
  };

  const updatePaymentStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/admin/laundry/order/${userInfo.userId}/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ payment_status: newStatus })
      });

      const data = await response.json();

      if (data.success) {
        await fetchLaundryOrders();
        await fetchLaundryStats();
        alert('Payment status updated successfully!');
        setDetailsModalOpen(false);
      } else {
        alert(data.message || 'Failed to update payment status');
      }
    } catch (err) {
      console.error('Error updating payment status:', err);
      alert('Failed to update payment status');
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/admin/laundry/order/${userInfo.userId}/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_status: newStatus })
      });

      const data = await response.json();

      if (data.success) {
        await fetchLaundryOrders();
        await fetchLaundryStats();
        alert('Order status updated successfully!');
        setDetailsModalOpen(false);
      } else {
        alert(data.message || 'Failed to update order status');
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      alert('Failed to update order status');
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setDetailsModalOpen(true);
  };

  const MenuItem = ({ emoji, label, active = false, route }) => (
    <div 
      className={`menu-item ${active ? 'menu-item-active' : ''}`}
      onClick={() => {
        if (route && onNavigate) {
          onNavigate(route);
        }
        setMobileMenuOpen(false);
      }}
    >
      <span className="menu-icon">{emoji}</span>
      <span className="menu-label">{label}</span>
      <div className="menu-glow"></div>
    </div>
  );

  const handleLogout = () => {
    sessionStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  const filteredOrders = laundryOrders.filter(order => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.student_name?.toLowerCase().includes(query) ||
      order.room_number?.toString().includes(query) ||
      order.service_type?.toLowerCase().includes(query)
    );
  });

  if (loading && laundryOrders.length === 0 && !stats) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(43,103,119,0.15)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f2f8f7',
            borderTop: '4px solid #2b6777',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: '#2b6777',
            fontSize: '16px',
            fontWeight: '600',
            margin: 0
          }}>Loading Laundry Orders...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%);
          overflow-x: hidden;
        }

        .dashboard-container {
          min-height: 100vh;
          display: flex;
          position: relative;
        }

        .sidebar {
          position: fixed;
          left: 0;
          top: 0;
          width: 280px;
          height: 100vh;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          box-shadow: 4px 0 24px rgba(43,103,119,0.1);
          z-index: 1000;
          display: flex;
          flex-direction: column;
          border-right: 2px solid rgba(82,171,152,0.15);
          transition: transform 0.3s ease;
        }

        .mobile-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 999;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s;
        }

        .mobile-overlay.show {
          opacity: 1;
          pointer-events: all;
        }

        .sidebar-header {
          padding: 28px 22px;
          background: linear-gradient(135deg, rgba(242,248,247,0.8) 0%, rgba(199,229,225,0.6) 100%);
          border-bottom: 2px solid rgba(82,171,152,0.15);
        }

        .sidebar-top {
          display: none;
          justify-content: flex-end;
          margin-bottom: 20px;
        }

        .sidebar-close-btn {
          background: rgba(43,103,119,0.08);
          border: 1.5px solid rgba(82,171,152,0.25);
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          width: 38px;
          height: 38px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .sidebar-close-btn:hover {
          background: rgba(43,103,119,0.15);
          border-color: #52ab98;
          transform: rotate(90deg);
        }

        .user-info-sidebar {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .user-avatar-sidebar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 22px;
          color: white;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
          border: 3px solid rgba(255, 255, 255, 0.5);
        }

        .user-details-sidebar {
          flex: 1;
        }

        .user-name-sidebar {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .user-role-sidebar {
          font-size: 13px;
          color: #52ab98;
          font-weight: 600;
        }

        .sidebar-nav {
          flex: 1;
          padding: 28px 20px;
          overflow-y: auto;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-nav::-webkit-scrollbar-track {
          background: rgba(82,171,152,0.08);
        }

        .sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 3px;
        }

        .nav-section {
          margin-bottom: 32px;
        }

        .nav-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: rgba(43,103,119,0.5);
          margin-bottom: 12px;
          padding-left: 18px;
        }

        .menu-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 18px;
          margin-bottom: 10px;
          cursor: pointer;
          color: #2b6777;
          font-size: 15px;
          font-weight: 600;
          border-radius: 14px;
          transition: all 0.3s;
          overflow: hidden;
        }

        .menu-glow {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .menu-item:hover {
          color: #52ab98;
          background: rgba(82,171,152,0.1);
          transform: translateX(6px);
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .menu-item:hover .menu-glow {
          opacity: 1;
        }

        .menu-item-active {
          background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
          color: #2b6777;
          box-shadow: 0 6px 20px rgba(82,171,152,0.2);
          font-weight: 700;
        }

        .menu-item-active .menu-glow {
          opacity: 1;
          animation: glow 2s ease-in-out infinite;
        }

        .menu-icon {
          font-size: 22px;
          position: relative;
          z-index: 1;
        }

        .menu-label {
          position: relative;
          z-index: 1;
        }

        .logout-section {
          padding: 20px;
          border-top: 2px solid rgba(82,171,152,0.15);
        }

        .logout-btn {
          width: 100%;
          padding: 16px 20px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .logout-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(43,103,119,0.4);
        }

        .main-content {
          flex: 1;
          margin-left: 280px;
          padding: 20px;
          width: calc(100% - 280px);
        }

        .top-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          padding: 18px 24px;
          border-radius: 18px;
          box-shadow: 0 6px 24px rgba(43,103,119,0.08);
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          border: 2px solid rgba(82,171,152,0.12);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 18px;
        }

        .mobile-menu-toggle {
          display: none;
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          border: 2px solid rgba(82,171,152,0.2);
          cursor: pointer;
          font-size: 22px;
          color: #2b6777;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .mobile-menu-toggle:hover {
          background: linear-gradient(135deg, rgba(43,103,119,0.2) 0%, rgba(82,171,152,0.2) 100%);
          border-color: #52ab98;
          transform: scale(1.05);
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .header-greeting {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .header-name {
          font-size: 20px;
          font-weight: 800;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .user-avatar-header {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 16px;
          color: white;
          box-shadow: 0 6px 20px rgba(43,103,119,0.25);
          border: 3px solid rgba(255, 255, 255, 0.8);
        }

        .page-title-section {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 24px;
          padding: 40px 36px;
          margin-bottom: 28px;
          box-shadow: 0 12px 48px rgba(43,103,119,0.3);
          position: relative;
          overflow: hidden;
        }

        .page-title-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 300px;
          height: 300px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          filter: blur(60px);
        }

        .page-title-content {
          position: relative;
          z-index: 1;
        }

        .page-title {
          font-size: 28px;
          font-weight: 900;
          color: white;
          margin-bottom: 10px;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 500;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
          margin-bottom: 28px;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 28px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          transition: all 0.3s;
          border: 2px solid rgba(82,171,152,0.12);
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 40px rgba(43,103,119,0.15);
          border-color: rgba(82,171,152,0.25);
        }

        .stat-card:hover::before {
          opacity: 1;
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 18px;
        }

        .stat-icon {
          width: 54px;
          height: 54px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 6px 20px rgba(43,103,119,0.15);
          transition: all 0.3s;
        }

        .stat-card:hover .stat-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .stat-body {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stat-label {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
          letter-spacing: -1px;
        }

        .filters-section {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          border: 2px solid rgba(82,171,152,0.12);
        }

        .filters-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 2fr;
          gap: 16px;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .filter-label {
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
        }

        .filter-select, .filter-input {
          padding: 12px 16px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 14px;
          font-family: inherit;
          transition: all 0.3s;
          background: white;
        }

        .filter-select:focus, .filter-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .orders-table-container {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 28px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          border: 2px solid rgba(82,171,152,0.12);
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .table-title {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
        }

        .table-count {
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          color: #2b6777;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          border: 2px solid rgba(82,171,152,0.2);
        }

        .orders-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0 12px;
        }

        .orders-table thead th {
          background: linear-gradient(135deg, rgba(242,248,247,0.8) 0%, rgba(199,229,225,0.6) 100%);
          padding: 16px;
          text-align: left;
          font-size: 13px;
          font-weight: 700;
          color: #2b6777;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: none;
        }

        .orders-table thead th:first-child {
          border-radius: 12px 0 0 12px;
        }

        .orders-table thead th:last-child {
          border-radius: 0 12px 12px 0;
        }

        .orders-table tbody tr {
          background: rgba(242,248,247,0.5);
          transition: all 0.3s;
          border-radius: 12px;
        }

        .orders-table tbody tr:hover {
          background: rgba(242,248,247,0.8);
          transform: translateX(6px);
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .orders-table tbody td {
          padding: 18px 16px;
          font-size: 14px;
          color: #2b6777;
          border: none;
        }

        .orders-table tbody td:first-child {
          border-radius: 12px 0 0 12px;
        }

        .orders-table tbody td:last-child {
          border-radius: 0 12px 12px 0;
        }

        .student-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .student-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 14px;
          color: white;
        }

        .student-details {
          display: flex;
          flex-direction: column;
        }

        .student-name {
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 2px;
        }

        .student-room {
          font-size: 12px;
          color: rgba(43,103,119,0.6);
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
        }

        .status-badge.pending {
          background: rgba(255, 177, 66, 0.15);
          color: #ffb142;
        }

        .status-badge.processing {
          background: rgba(52,172,224,0.15);
          color: #34ace0;
        }

        .status-badge.completed {
          background: rgba(46, 213, 115, 0.15);
          color: #2ed573;
        }

        .status-badge.paid {
          background: rgba(46, 213, 115, 0.15);
          color: #2ed573;
        }

        .status-badge.unpaid {
          background: rgba(255, 71, 87, 0.15);
          color: #ff4757;
        }

        .service-badge {
          display: inline-flex;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          background: rgba(147,112,219,0.15);
          color: #9370db;
        }

        .action-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          background: linear-gradient(135deg, rgba(52,172,224,0.15) 0%, rgba(52,172,224,0.25) 100%);
          color: #34ace0;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 20px;
          max-width: 600px;
          width: 100%;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(43,103,119,0.3);
        }

        .modal-header {
          padding: 24px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-title {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
        }

        .modal-close {
          background: rgba(43,103,119,0.08);
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 18px;
          color: #2b6777;
          transition: all 0.3s;
        }

        .modal-close:hover {
          background: rgba(43,103,119,0.15);
          transform: rotate(90deg);
        }

        .modal-body {
          padding: 24px;
        }

        .modal-section {
          margin-bottom: 24px;
        }

        .modal-section-title {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 2px solid rgba(82,171,152,0.2);
        }

        .modal-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        .modal-row {
          margin-bottom: 16px;
        }

        .modal-label {
          font-size: 12px;
          font-weight: 700;
          color: rgba(43,103,119,0.6);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }

        .modal-value {
          font-size: 15px;
          font-weight: 600;
          color: #2b6777;
        }

        .modal-footer {
          padding: 24px;
          border-top: 2px solid rgba(82,171,152,0.15);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .modal-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        .modal-btn.primary {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
        }

        .modal-btn.success {
          background: linear-gradient(135deg, #2ed573 0%, #2ed573 100%);
          color: white;
        }

        .modal-btn.secondary {
          background: rgba(82,171,152,0.1);
          color: #2b6777;
        }

        .modal-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .no-data-message {
          text-align: center;
          padding: 60px 20px;
          color: rgba(43,103,119,0.5);
        }

        .no-data-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .no-data-text {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .no-data-subtext {
          font-size: 14px;
        }

        @keyframes glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        @media (max-width: 1024px) {
          .sidebar {
            transform: translateX(-100%);
          }

          .sidebar.mobile-open {
            transform: translateX(0);
          }

          .mobile-overlay {
            display: block;
          }

          .sidebar-top {
            display: flex;
          }

          .mobile-menu-toggle {
            display: flex;
          }

          .main-content {
            margin-left: 0;
            width: 100%;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .filters-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 16px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .page-title {
            font-size: 24px;
          }

          .orders-table-container {
            overflow-x: auto;
          }

          .orders-table {
            min-width: 900px;
          }
        }
      `}</style>

      <div className="dashboard-container">
        <div 
          className={`mobile-overlay ${mobileMenuOpen ? 'show' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        />

        <div className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
              <button onClick={() => setMobileMenuOpen(false)} className="sidebar-close-btn">
                ✕
              </button>
            </div>
            <div className="user-info-sidebar">
              <div className="user-avatar-sidebar">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
              <div className="user-details-sidebar">
                <div className="user-name-sidebar">{dashboardData?.adminName || 'Admin User'}</div>
                <div className="user-role-sidebar">
                  {dashboardData?.hostelName || 'Hostel Administrator'}
                </div>
              </div>
            </div>
          </div>

          <div className="sidebar-nav">
            {getPrivilegesByCategory('overview').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Overview</div>
                {getPrivilegesByCategory('overview').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {getPrivilegesByCategory('management').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Management</div>
                {getPrivilegesByCategory('management').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    active={privilege.page_route === 'hostel-admin-laundry'}
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {getPrivilegesByCategory('approval').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Approval</div>
                {getPrivilegesByCategory('approval').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {getPrivilegesByCategory('settings').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Settings</div>
                {getPrivilegesByCategory('settings').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {getPrivilegesByCategory('reports').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Reports</div>
                {getPrivilegesByCategory('reports').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {userPrivileges.length === 0 && !privilegesLoading && (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                background: 'rgba(255,249,196,0.3)',
                borderRadius: '16px',
                border: '2px dashed rgba(255,193,7,0.4)',
                margin: '20px'
              }}>
                <div style={{fontSize: '48px', marginBottom: '16px'}}>🔒</div>
                <div style={{fontSize: '16px', color: '#2b6777', fontWeight: '600', marginBottom: '8px'}}>No Access Granted</div>
                <div style={{fontSize: '14px', color: 'rgba(43,103,119,0.7)'}}>Please contact administrator</div>
              </div>
            )}
          </div>

          <div className="logout-section">
            <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button>
          </div>
        </div>

        <div className="main-content">
          <div className="top-header">
            <div className="header-left">
              <button 
                className="mobile-menu-toggle" 
                onClick={() => setMobileMenuOpen(true)}
              >
                ☰
              </button>
              <div className="header-title-section">
                <div className="header-greeting">Laundry Management</div>
                <div className="header-name">{dashboardData?.adminName || 'Admin'}</div>
              </div>
            </div>
            <div className="header-right">
              <div className="user-avatar-header">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
            </div>
          </div>

          <div className="page-title-section">
            <div className="page-title-content">
              <div className="page-title">Laundry Service Orders</div>
              <div className="page-subtitle">Manage and track student laundry service requests</div>
            </div>
          </div>

          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, rgba(52,172,224,0.15) 0%, rgba(52,172,224,0.25) 100%)' }}>
                    📋
                  </div>
                </div>
                <div className="stat-body">
                  <div className="stat-label">Total Orders</div>
                  <div className="stat-value">{stats?.total_orders || stats?.totalOrders || 0}</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, rgba(255,177,66,0.15) 0%, rgba(255,177,66,0.25) 100%)' }}>
                    ⏳
                  </div>
                </div>
                <div className="stat-body">
                  <div className="stat-label">Pending</div>
                  <div className="stat-value">{stats?.pending_orders || stats?.pendingOrders || 0}</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, rgba(52,172,224,0.15) 0%, rgba(52,172,224,0.25) 100%)' }}>
                    🔄
                  </div>
                </div>
                <div className="stat-body">
                  <div className="stat-label">Processing</div>
                  <div className="stat-value">{stats?.in_progress_orders || stats?.processingOrders || 0}</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, rgba(46,213,115,0.15) 0%, rgba(46,213,115,0.25) 100%)' }}>
                    ✅
                  </div>
                </div>
                <div className="stat-body">
                  <div className="stat-label">Completed</div>
                  <div className="stat-value">{stats?.completed_orders || stats?.completedOrders || 0}</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, rgba(46,213,115,0.15) 0%, rgba(46,213,115,0.25) 100%)' }}>
                    💰
                  </div>
                </div>
                <div className="stat-body">
                  <div className="stat-label">Total Revenue</div>
                  <div className="stat-value">₹{stats?.total_revenue || stats?.totalRevenue || 0}</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, rgba(255,71,87,0.15) 0%, rgba(255,71,87,0.25) 100%)' }}>
                    ⚠️
                  </div>
                </div>
                <div className="stat-body">
                  <div className="stat-label">Unpaid</div>
                  <div className="stat-value">{stats?.pending_payments || stats?.unpaidOrders || 0}</div>
                </div>
              </div>
            </div>
          )}

          <div className="filters-section">
            <div className="filters-grid">
              <div className="filter-group">
                <label className="filter-label">Service Type</label>
                <select
                  className="filter-select"
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                >
                  <option value="all">All Services</option>
                  <option value="wash">Wash Only</option>
                  <option value="iron">Iron Only</option>
                  <option value="wash_iron">Wash + Iron</option>
                </select>
              </div>
              <div className="filter-group">
                <label className="filter-label">Payment Status</label>
                <select
                  className="filter-select"
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                >
                  <option value="all">All Payments</option>
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                </select>
              </div>
              <div className="filter-group">
                <label className="filter-label">Order Status</label>
                <select
                  className="filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="filter-group">
                <label className="filter-label">Search Orders</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Search by student, room, service..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="orders-table-container">
            <div className="table-header">
              <div className="table-title">Laundry Orders</div>
              <div className="table-count">{filteredOrders.length} Orders</div>
            </div>

            {filteredOrders.length > 0 ? (
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Service</th>
                    <th>Items</th>
                    <th>Amount</th>
                    <th>Order Status</th>
                    <th>Payment</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.order_id}>
                      <td>
                        <div className="student-info">
                          <div className="student-avatar">
                            {order.student_name ? order.student_name.charAt(0).toUpperCase() : 'S'}
                          </div>
                          <div className="student-details">
                            <div className="student-name">{order.student_name || 'Unknown'}</div>
                            <div className="student-room">Room {order.room_number || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="service-badge">{order.service_type || 'N/A'}</span>
                      </td>
                      <td>{order.item_count || 0} items</td>
                      <td>₹{order.total_amount || 0}</td>
                      <td>
                        {(() => {
                          const orderStatus = order.order_status || order.status || order.orderStatus || 'pending';
                          const statusDisplay = {
                            'pending': '⏳ Pending',
                            'processing': '🔄 Processing', 
                            'completed': '✅ Completed',
                            'delivered': '📦 Delivered',
                            'cancelled': '❌ Cancelled'
                          };
                          
                          return (
                            <span className={`status-badge ${orderStatus}`}>
                              {statusDisplay[orderStatus] || `📋 ${orderStatus.charAt(0).toUpperCase() + orderStatus.slice(1)}`}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        <span className={`status-badge ${order.payment_status || 'unpaid'}`}>
                          {order.payment_status === 'paid' ? '✅ Paid' : '❌ Unpaid'}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="action-btn"
                          onClick={() => viewOrderDetails(order)}
                        >
                          👁️ View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data-message">
                <div className="no-data-icon">🧺</div>
                <div className="no-data-text">No Laundry Orders Found</div>
                <div className="no-data-subtext">
                  {searchQuery ? 'Try adjusting your search' : 'No orders available'}
                </div>
              </div>
            )}
          </div>
        </div>

        {detailsModalOpen && selectedOrder && (
          <div className="modal-overlay" onClick={() => setDetailsModalOpen(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">Laundry Order Details</div>
                <button className="modal-close" onClick={() => setDetailsModalOpen(false)}>
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <div className="modal-section">
                  <div className="modal-section-title">Student Information</div>
                  <div className="modal-grid">
                    <div className="modal-row">
                      <div className="modal-label">Student Name</div>
                      <div className="modal-value">{selectedOrder.student_name}</div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">Room Number</div>
                      <div className="modal-value">{selectedOrder.room_number}</div>
                    </div>
                  </div>
                </div>

                <div className="modal-section">
                  <div className="modal-section-title">Order Details</div>
                  <div className="modal-grid">
                    <div className="modal-row">
                      <div className="modal-label">Service Type</div>
                      <div className="modal-value">{selectedOrder.service_type}</div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">Item Count</div>
                      <div className="modal-value">{selectedOrder.item_count} items</div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">Total Amount</div>
                      <div className="modal-value">₹{selectedOrder.total_amount}</div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">Order Date</div>
                      <div className="modal-value">
                        {new Date(selectedOrder.order_date).toLocaleString()}
                      </div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">Order Status</div>
                      <div className="modal-value">
                        <select 
                          value={selectedOrder.order_status || selectedOrder.status || 'pending'}
                          onChange={(e) => updateOrderStatus(selectedOrder.order_id, e.target.value)}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: '2px solid rgba(82,171,152,0.3)',
                            background: 'white',
                            color: '#2b6777',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'all 0.2s'
                          }}
                        >
                          <option value="pending">⏳ Pending</option>
                          <option value="processing">🔄 Processing</option>
                          <option value="completed">✅ Completed</option>
                          <option value="delivered">📦 Delivered</option>
                          <option value="cancelled">❌ Cancelled</option>
                        </select>
                      </div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">Payment Status</div>
                      <div className="modal-value">
                        <span className={`status-badge ${selectedOrder.payment_status}`}>
                          {selectedOrder.payment_status === 'paid' ? '✅ Paid' : '❌ Unpaid'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedOrder.special_instructions && (
                  <div className="modal-section">
                    <div className="modal-section-title">Special Instructions</div>
                    <div className="modal-row">
                      <div className="modal-value" style={{ 
                        background: 'rgba(242,248,247,0.5)',
                        padding: '16px',
                        borderRadius: '12px',
                        border: '2px solid rgba(82,171,152,0.2)'
                      }}>
                        {selectedOrder.special_instructions}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {selectedOrder.status !== 'completed' && (
                  <>
                    {selectedOrder.status === 'pending' && (
                      <button 
                        className="modal-btn primary"
                        onClick={() => updateOrderStatus(selectedOrder.order_id, 'processing')}
                      >
                        🔄 Mark as Processing
                      </button>
                    )}
                    {selectedOrder.status === 'processing' && (
                      <button 
                        className="modal-btn success"
                        onClick={() => updateOrderStatus(selectedOrder.order_id, 'completed')}
                      >
                        ✅ Mark as Completed
                      </button>
                    )}
                  </>
                )}
                {selectedOrder.payment_status === 'unpaid' && (
                  <button 
                    className="modal-btn success"
                    onClick={() => updatePaymentStatus(selectedOrder.order_id, 'paid')}
                  >
                    💰 Mark as Paid
                  </button>
                )}
                <button className="modal-btn secondary" onClick={() => setDetailsModalOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HostelLaundry;